// src/crm/campaigns/CampaignForm.tsx
import { useState } from 'react'
import type { Campaign } from '../../lib/types'
import type { CampaignInput } from '../hooks/useCampaigns'

const STATUS_OPTIONS: { value: Campaign['status']; label: string }[] = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'done', label: 'Terminée' },
]

interface CampaignFormProps {
  initial?: Campaign | undefined
  onSubmit: (data: CampaignInput) => void
  onCancel: () => void
  isPending: boolean
  error: string | null
}

export function CampaignForm({ initial, onSubmit, onCancel, isPending, error }: CampaignFormProps) {
  const [name, setName] = useState(initial?.name ?? '')
  const [goal, setGoal] = useState(initial?.goal ?? '')
  const [budgetAmount, setBudgetAmount] = useState(
    initial?.budgetAmount !== null && initial?.budgetAmount !== undefined
      ? String(initial.budgetAmount)
      : '',
  )
  const [currency, setCurrency] = useState(initial?.currency ?? 'EUR')
  const [status, setStatus] = useState<Campaign['status']>(initial?.status ?? 'planning')
  const [startDate, setStartDate] = useState(initial?.startDate ?? '')
  const [endDate, setEndDate] = useState(initial?.endDate ?? '')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmit({
      name: name.trim(),
      goal: goal.trim() || null,
      budgetAmount: budgetAmount !== '' ? parseFloat(budgetAmount) : null,
      currency,
      status,
      startDate: startDate || null,
      endDate: endDate || null,
    })
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {error && (
        <div style={{
          background: 'rgba(217,83,30,0.12)',
          border: '1px solid var(--arancio)',
          borderRadius: '6px',
          padding: '0.625rem 0.875rem',
          fontSize: '0.875rem',
          color: 'var(--arancio)',
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        <label htmlFor="campaign-name" style={{ fontSize: '0.8125rem', color: 'var(--muted)', fontWeight: 500 }}>
          Nom <span style={{ color: 'var(--arancio)' }}>*</span>
        </label>
        <input
          id="campaign-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="Lancement Basalt Lab v2"
          style={inputStyle}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        <label htmlFor="campaign-goal" style={{ fontSize: '0.8125rem', color: 'var(--muted)', fontWeight: 500 }}>
          Objectif
        </label>
        <input
          id="campaign-goal"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="500 installs, 2M vues…"
          style={inputStyle}
        />
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label htmlFor="campaign-budget" style={{ fontSize: '0.8125rem', color: 'var(--muted)', fontWeight: 500 }}>
            Budget
          </label>
          <input
            id="campaign-budget"
            type="number"
            value={budgetAmount}
            onChange={(e) => setBudgetAmount(e.target.value)}
            min={0}
            step={0.01}
            placeholder="0"
            style={inputStyle}
          />
        </div>
        <div style={{ width: '90px', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label htmlFor="campaign-currency" style={{ fontSize: '0.8125rem', color: 'var(--muted)', fontWeight: 500 }}>
            Devise
          </label>
          <input
            id="campaign-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            maxLength={3}
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        <label htmlFor="campaign-status" style={{ fontSize: '0.8125rem', color: 'var(--muted)', fontWeight: 500 }}>
          Statut
        </label>
        <select
          id="campaign-status"
          value={status}
          onChange={(e) => setStatus(e.target.value as Campaign['status'])}
          style={inputStyle}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label htmlFor="campaign-start" style={{ fontSize: '0.8125rem', color: 'var(--muted)', fontWeight: 500 }}>
            Date de début
          </label>
          <input
            id="campaign-start"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label htmlFor="campaign-end" style={{ fontSize: '0.8125rem', color: 'var(--muted)', fontWeight: 500 }}>
            Date de fin
          </label>
          <input
            id="campaign-end"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
        <button type="button" onClick={onCancel} style={btnSecondary}>
          Annuler
        </button>
        <button type="submit" disabled={isPending || !name.trim()} style={btnPrimary}>
          {isPending ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </form>
  )
}

const inputStyle: React.CSSProperties = {
  background: 'var(--elevated)',
  border: '1px solid var(--line-strong)',
  borderRadius: '6px',
  padding: '0.4375rem 0.625rem',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  outline: 'none',
  width: '100%',
}

const btnPrimary: React.CSSProperties = {
  background: 'var(--arancio)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '0.4375rem 1rem',
  fontSize: '0.875rem',
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
}

const btnSecondary: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--muted)',
  border: '1px solid var(--line-strong)',
  borderRadius: '6px',
  padding: '0.4375rem 1rem',
  fontSize: '0.875rem',
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
}
